import { format } from 'date-fns';
import { es, enUS } from 'date-fns/locale';
import i18n from '../i18n';

// Función para capitalizar la primera letra de un string
const capitalizeFirstLetter = (string) => {
  return string.charAt(0).toUpperCase() + string.slice(1);
};

const getEffectiveLanguage = (language) => {
  return language || i18n.resolvedLanguage || i18n.languages?.[0] || i18n.language || 'es';
};

export const getNormalizedLanguage = (language) => {
  const effectiveLanguage = getEffectiveLanguage(language);
  return effectiveLanguage.split('-')[0].toLowerCase();
};

export const isSpanishLanguage = (language) => {
  return getNormalizedLanguage(language) === 'es';
};

export const getDateFnsLocale = (language) => {
  return isSpanishLanguage(language) ? es : enUS;
};

export const getIntlLocale = (language) => {
  return isSpanishLanguage(language) ? 'es-ES' : 'en-US';
};

// Función para obtener el locale según el idioma actual
const getLocale = (language) => {
  return getDateFnsLocale(language);
};

const getValidDate = (value) => {
  if (!value || value === 'Invalid Date') {
    return null;
  }

  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
};

// Función para formatear partialDateStart
export const formatPartialDateStart = (startDate, language) => {
  const start = getValidDate(startDate);
  if (!start) {
    return '';
  }

  const locale = getLocale(language);
  const formattedDate = format(start, 'EEEE d MMM yyyy', { locale });
  return capitalizeFirstLetter(formattedDate);
};

// Función para formatear partialDateEnd
export const formatPartialDateEnd = (startDate, endDate, language) => {
  const start = getValidDate(startDate);
  const end = getValidDate(endDate);
  if (!start || !end) {
    return '';
  }

  const locale = getLocale(language);

  let formattedEndDate;

  // Si el evento termina el mismo día
  if (start.getDate() === end.getDate()) {
    formattedEndDate = format(end, 'H:mm', { locale });
  } else if (start.getFullYear() === end.getFullYear()) {
    // Si el evento dura más de un día, pero dentro del mismo año
    formattedEndDate = format(end, 'd MMM H:mm', { locale });
  } else {
    // Si el evento dura más de un año
    formattedEndDate = format(end, 'd MMM yyyy H:mm', { locale });
  }

  return `${format(start, 'H:mm', { locale })} - ${formattedEndDate}`;
};

// Función para formatear dayDate
export const formatDayDate = (dayDateOrStartDate) => {
  if (!dayDateOrStartDate) {
    return '';
  }

  // Si es un número o string que representa solo el día (como "30")
  if (!isNaN(dayDateOrStartDate) || /^\d+$/.test(dayDateOrStartDate)) {
    return dayDateOrStartDate.toString();
  }

  // Si es una fecha completa
  const date = getValidDate(dayDateOrStartDate);
  if (date) {
    return format(date, 'd');
  }

  return '';
};

// Función para formatear monthDate
export const formatMonthDate = (monthDateOrStartDate, language) => {
  if (!monthDateOrStartDate) {
    return '';
  }

  const date = getValidDate(monthDateOrStartDate);
  if (date) {
    const locale = getLocale(language);
    const month = format(date, 'MMM', { locale });
    return capitalizeFirstLetter(month);
  }

  // Compatibilidad con valores legacy del backend
  if (typeof monthDateOrStartDate === 'string' && monthDateOrStartDate.length <= 4) {
    return capitalizeFirstLetter(monthDateOrStartDate);
  }

  return '';
};

// Función para formatear weekdayStart
export const formatWeekdayStart = (startDate, language) => {
  const start = getValidDate(startDate);
  if (!start) {
    return '';
  }

  const locale = getLocale(language);
  const weekday = format(start, 'EEEE', { locale });
  return capitalizeFirstLetter(weekday);
};

// Función para formatear longDate
export const formatLongDate = (startDate, endDate, language) => {
  const start = getValidDate(startDate);
  if (!start) {
    return '';
  }

  const locale = getLocale(language);

  const end = getValidDate(endDate);
  if (!end) {
    // Solo fecha de inicio
    return capitalizeFirstLetter(format(start, 'EEEE d MMMM yyyy', { locale }));
  }

  // Si es el mismo día
  if (start.toDateString() === end.toDateString()) {
    return capitalizeFirstLetter(format(start, 'EEEE d MMMM yyyy', { locale }));
  }

  // Si son días diferentes
  return `${capitalizeFirstLetter(format(start, 'EEEE d MMMM yyyy', { locale }))} - ${capitalizeFirstLetter(format(end, 'EEEE d MMMM yyyy', { locale }))}`;
};

// Función para verificar si un evento ha pasado
export const isEventPast = (endDate) => {
  if (!endDate || endDate === 'Invalid Date' || isNaN(new Date(endDate))) {
    return false; // Si no hay fecha válida, asumimos que no ha pasado
  }
  
  // Usar UTC para evitar problemas de zona horaria
  const now = new Date();
  const eventEnd = new Date(endDate);
  
  // Convertir ambas fechas a UTC para comparación consistente
  const nowUTC = new Date(now.getTime() + (now.getTimezoneOffset() * 60000));
  const eventEndUTC = new Date(eventEnd.getTime() + (eventEnd.getTimezoneOffset() * 60000));
  
  return eventEndUTC <= nowUTC;
};

// Función para calcular el tiempo transcurrido desde que pasó el evento
export const getTimeSinceEventPassed = (endDate, language = 'es') => {
  const now = new Date();
  const eventEnd = new Date(endDate);
  const diffInMs = now - eventEnd;
  
  if (diffInMs <= 0) return null; // Event hasn't ended yet
  
  const diffInMinutes = Math.floor(diffInMs / (1000 * 60));
  const diffInHours = Math.floor(diffInMs / (1000 * 60 * 60));
  const diffInDays = Math.floor(diffInMs / (1000 * 60 * 60 * 24));
  
  // Si terminó hace menos de 1 hora
  if (diffInMinutes < 60) {
    return i18n.t('eventDetail:events.minutesAgo', { count: diffInMinutes });
  }
  // Si terminó hace menos de 24 horas (mismo día o día anterior)
  else if (diffInHours < 24) {
    return i18n.t('eventDetail:events.hoursAgo', { count: diffInHours });
  }
  // Si terminó ayer
  else if (diffInDays === 1) {
    return i18n.t('eventDetail:events.yesterday');
  }
  // Si terminó hace menos de una semana
  else if (diffInDays < 7) {
    return i18n.t('eventDetail:events.daysAgo', { count: diffInDays });
  }
  // Si terminó hace menos de un mes
  else if (diffInDays < 30) {
    const weeks = Math.floor(diffInDays / 7);
    return i18n.t('eventDetail:events.weeksAgo', { count: weeks });
  }
  // Si terminó hace menos de un año
  else if (diffInDays < 365) {
    const months = Math.floor(diffInDays / 30);
    return i18n.t('eventDetail:events.monthsAgo', { count: months });
  }
  // Si terminó hace más de un año
  else {
    const years = Math.floor(diffInDays / 365);
    return i18n.t('eventDetail:events.yearsAgo', { count: years });
  }
};

export const formatShortDateTime = (dateValue, language, options = {}) => {
  const date = getValidDate(dateValue);
  if (!date) {
    return '';
  }

  const locale = getLocale(language);
  const isSpanish = isSpanishLanguage(language);
  const {
    dateStyle = 'abbreviated',
    includeComma = false,
  } = options;

  let datePattern = isSpanish ? 'dd MMM yyyy' : 'MMM dd, yyyy';
  if (dateStyle === 'numeric') {
    datePattern = isSpanish ? 'dd/MM/yyyy' : 'MM/dd/yyyy';
  }

  const separator = includeComma && dateStyle === 'abbreviated' && !isSpanish ? ' ' : includeComma ? ', ' : ' ';
  return `${format(date, datePattern, { locale })}${separator}${format(date, 'HH:mm', { locale })}`;
};

export const formatNotificationTimestamp = (dateValue, language) => {
  const date = getValidDate(dateValue);
  if (!date) {
    return '';
  }

  return formatShortDateTime(date, language, {
    dateStyle: isSpanishLanguage(language) ? 'numeric' : 'abbreviated',
    includeComma: true,
  });
};

export const formatEventDateDisplay = (startDate, endDate, language) => {
  const formattedRange = formatEventDateRange(startDate, endDate, language);
  if (formattedRange.singleLine) {
    return formattedRange.singleLine;
  }

  if (!formattedRange.startLine || !formattedRange.endLine) {
    return '';
  }

  return `${formattedRange.startLine} - ${formattedRange.endLine}`;
};

export const formatFixedEventDate = (startDate, endDate, language) => {
  const start = getValidDate(startDate);
  if (!start) {
    return '';
  }

  const end = getValidDate(endDate);
  const locale = getLocale(language);
  const dateTimePattern = isSpanishLanguage(language) ? 'd MMM yyyy HH:mm' : 'MMM d, yyyy HH:mm';

  if (!end) {
    return format(start, dateTimePattern, { locale });
  }

  const sameDay = start.toDateString() === end.toDateString();
  if (sameDay) {
    return `${format(start, dateTimePattern, { locale })} - ${format(end, 'HH:mm', { locale })}`;
  }

  return `${format(start, dateTimePattern, { locale })} - ${format(end, dateTimePattern, { locale })}`;
};

export const formatTimeOnly = (dateValue, language) => {
  const date = getValidDate(dateValue);
  if (!date) {
    return '';
  }

  return format(date, 'HH:mm', { locale: getLocale(language) });
};

// Función para formatear fechas de inicio y fin según los requisitos específicos
export const formatEventDateRange = (startDate, endDate, language) => {
  const start = getValidDate(startDate);
  const end = getValidDate(endDate);
  if (!start || !end) {
    return { singleLine: '', startLine: '', endLine: '' };
  }

  const locale = getLocale(language);

  // Verificar si es el mismo día
  const isSameDay = start.getDate() === end.getDate() &&
    start.getMonth() === end.getMonth() &&
    start.getFullYear() === end.getFullYear();

  if (isSameDay) {
    // Mismo día, diferentes horas: "Martes 23 sep 2025, 16:30 - 17:30"
    const dayName = capitalizeFirstLetter(format(start, 'EEEE', { locale }));
    const dayNumber = format(start, 'd', { locale });
    const monthName = format(start, 'MMM', { locale }).toLowerCase();
    const year = format(start, 'yyyy', { locale });
    const startTime = format(start, 'H:mm', { locale });
    const endTime = format(end, 'H:mm', { locale });

    return {
      singleLine: `${dayName} ${dayNumber} ${monthName} ${year}, ${startTime} - ${endTime}`,
      startLine: '',
      endLine: ''
    };
  } else {
    // Diferentes días: "Martes 23 sep 2025, 16:30" y "24 sep 2025, 17:30"
    const startDayName = capitalizeFirstLetter(format(start, 'EEEE', { locale }));
    const startDayNumber = format(start, 'd', { locale });
    const startMonthName = format(start, 'MMM', { locale }).toLowerCase();
    const startYear = format(start, 'yyyy', { locale });
    const startTime = format(start, 'H:mm', { locale });

    const endDayNumber = format(end, 'd', { locale });
    const endMonthName = format(end, 'MMM', { locale }).toLowerCase();
    const endYear = format(end, 'yyyy', { locale });
    const endTime = format(end, 'H:mm', { locale });

    // Si es el mismo año, no repetir el año en la fecha de fin
    const endYearDisplay = startYear === endYear ? '' : ` ${endYear}`;

    return {
      singleLine: '',
      startLine: `${startDayName} ${startDayNumber} ${startMonthName} ${startYear}, ${startTime}`,
      endLine: `${endDayNumber} ${endMonthName}${endYearDisplay}, ${endTime}`
    };
  }
};
